import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Avatar } from '../components/Avatar'
import { YouthFeelingCheckIn } from '../components/YouthFeelingCheckIn'
import { YouthNixiVideo } from '../components/YouthNixiVideo'
import { useApp } from '../store/AppContext'
import { formatTime12Hour } from '../lib/time'
import { availableFoodBites, earnedChildPoints, loadCompanionState } from '../lib/youthRewards'
import { isYouthDemoProfile, setYouthDemoProfile, youthDemoProfile } from '../lib/youthDemo'
import { youthVisibleEvents } from '../lib/youthCalendar'
import { loadYouthSupportPlan } from '../lib/youthSupportPlan'

const regulationMessages: Record<string, string> = {
  green: 'You’re feeling ready. We can keep going at your pace. 🌿',
  amber: 'Something might be tricky. Want some help? 💛',
  red: 'We can stop. You do not have to rush. ❤️',
}

const morningGreetings = [
  "It’s going to be a great day! ☀️",
  "You’ve got this! 💪",
  "Let’s do all the things today! 🌟",
  'Nixi is ready to roll! 🧡',
  'A brand new day is waiting for you! ✨',
]

const afternoonGreetings = [
  'You’re doing great today! 🌟',
  'Keep going. You’ve got this! 💪',
  'Nixi is right here with you! 🧡',
  'What shall we tackle next? 🚀',
  'You’ve already done so much today! ✨',
]

const eveningGreetings = [
  'Did you have a good day? 🌙',
  "Don’t forget to put Nixi to bed! 🧸",
  'You did a lot today. Well done! ⭐',
  'Time to slow things down and get cosy. 💜',
  'Nixi is getting sleepy too! 😴',
]

type YouthHomeMoment = 'subtle' | 'wave' | 'flying' | 'celebrate'

const homeRotation: YouthHomeMoment[] = ['wave', 'flying', 'celebrate', 'subtle']
const homeSegmentDurations: Record<YouthHomeMoment, number> = {
  subtle: 1500,
  wave: 1500,
  flying: 2000,
  celebrate: 1950,
}
const pinkHomeDurations: Record<YouthHomeMoment, number> = {
  subtle: 5000,
  wave: 5000,
  flying: 5000,
  celebrate: 4200,
}

function greetingForTime(hour: number) {
  const options = hour < 12 ? morningGreetings : hour < 17 ? afternoonGreetings : eveningGreetings
  return options[Math.floor(Math.random() * options.length)]
}

function nixiGreetingForTime(hour: number) {
  if (hour < 12) return 'Good morning! I’m getting ready with you today!'
  if (hour < 17) return 'I’m here with you. What would you like to do next?'
  return 'Good evening! We can slow down and finish the day together.'
}

function speak(text: string) {
  if (!('speechSynthesis' in window)) return false
  window.speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.rate = 0.9
  window.speechSynthesis.speak(utterance)
  return true
}

export function YouthToday() {
  const { currentUser, events, allUsers, setCurrentUserId, setAccountType } = useApp()
  const participantId = currentUser.id === 'u-jnr' ? 'u-jnr' : 'u-leo'
  const navigate = useNavigate()
  const [plan, setPlan] = useState(() => loadYouthSupportPlan(participantId))
  const [companionMessage, setCompanionMessage] = useState(() => nixiGreetingForTime(new Date().getHours()))
  const [companion, setCompanion] = useState(() => loadCompanionState(participantId))
  const [homeMoment, setHomeMoment] = useState<YouthHomeMoment>('wave')
  const [showTellSomeone, setShowTellSomeone] = useState(false)
  const [helpRequest, setHelpRequest] = useState<string | null>(null)
  const [, setRoutineVersion] = useState(0)
  const demoChild = isYouthDemoProfile()
  const firstName = demoChild ? youthDemoProfile.name : currentUser.name.split(' ')[0]
  const bites = availableFoodBites(participantId)
  const points = earnedChildPoints(participantId)
  const motivationalGreeting = useMemo(() => greetingForTime(new Date().getHours()), [])

  const exitChildView = () => {
    setYouthDemoProfile(false)
    setAccountType('youth')
    setCurrentUserId('u-lillian')
    navigate('/home')
  }

  const todaysEvents = youthVisibleEvents(events, allUsers, currentUser.id)
    .filter((event) => event.date === new Date().toLocaleDateString('en-CA'))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
  const supportEvents = todaysEvents.filter((event) => event.type === 'shift')
  const demoWorkers = allUsers.filter((user) => user.role === 'support_worker').slice(0, 2)

  const nextTile = useMemo(() => {
    const hour = new Date().getHours()
    const wanted = hour < 9 ? 'morning' : hour < 15 ? 'school' : hour < 19 ? 'home' : 'bedtime'
    return plan.tiles.find((tile) => tile.id === wanted) ?? plan.tiles[0]
  }, [plan])

  const dateLabel = new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())

  useEffect(() => {
    const durations = companion.colour === 'pink' ? pinkHomeDurations : homeSegmentDurations
    const timer = window.setTimeout(() => {
      setHomeMoment((current) => homeRotation[(homeRotation.indexOf(current) + 1) % homeRotation.length])
    }, durations[homeMoment])
    return () => window.clearTimeout(timer)
  }, [companion.colour, homeMoment])

  useEffect(() => {
    setCompanion(loadCompanionState(participantId))
    setHomeMoment('wave')
  }, [participantId])

  useEffect(() => {
    const refresh = () => setPlan(loadYouthSupportPlan(participantId))
    const refreshRoutine = () => {
      setRoutineVersion((value) => value + 1)
      const nextCompanion = loadCompanionState(participantId)
      setCompanion((current) => {
        if (current.colour !== nextCompanion.colour) setHomeMoment('wave')
        return nextCompanion
      })
    }
    window.addEventListener('schub:youth-support-plan', refresh)
    window.addEventListener('schub:youth-rewards', refreshRoutine)
    window.addEventListener('storage', refreshRoutine)
    return () => {
      window.removeEventListener('schub:youth-support-plan', refresh)
      window.removeEventListener('schub:youth-rewards', refreshRoutine)
      window.removeEventListener('storage', refreshRoutine)
    }
  }, [participantId])

  useEffect(() => {
    const onRegulation = (event: Event) => {
      const detail = (event as CustomEvent<{ userId: string; state: string }>).detail
      if (!detail || detail.userId !== currentUser.id) return
      setCompanionMessage(regulationMessages[detail.state] ?? 'Thanks for telling me how your body feels.')
    }
    const onSupport = (event: Event) => {
      const detail = (event as CustomEvent<{ userId: string; label?: string }>).detail
      if (!detail || detail.userId !== currentUser.id) return
      setCompanionMessage(`${detail.label ?? 'That'} sounds like a good choice. I’m here with you. 💜`)
    }
    window.addEventListener('schub:youth-regulation', onRegulation)
    window.addEventListener('schub:youth-regulation-support', onSupport)
    return () => {
      window.removeEventListener('schub:youth-regulation', onRegulation)
      window.removeEventListener('schub:youth-regulation-support', onSupport)
    }
  }, [currentUser.id])

  const chooseHelp = (id: string) => {
    setHelpRequest(id)
    const label = plan.tellSomeoneChoices.find((choice) => choice.id === id)?.label ?? 'I need support'
    const spoken = speak(label)
    setCompanionMessage(spoken ? `I said: “${label}”. I’m here with you. 💜` : `${label}. Thanks for telling someone. 💜`)
  }

  const visibleWorkers = supportEvents.length > 0
    ? supportEvents.map((event) => ({ event, worker: event.assignees.map((a) => allUsers.find((u) => u.id === a.userId)).find((u) => u?.role === 'support_worker') })).filter((item) => item.worker)
    : demoChild ? demoWorkers.map((worker, index) => ({ worker, event: { id: `demo-worker-${index}`, startTime: index === 0 ? '09:00' : '15:00', endTime: index === 0 ? '13:00' : '17:00' } })) : []

  const rewardProgress = Math.min(100, Math.round((points / Math.max(1, plan.rewardGoal.targetPoints)) * 100))

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-violet-50 to-amber-50 pb-28">
      <div className="mx-auto w-full max-w-5xl px-4 pt-5 sm:px-6 lg:px-8">
        {demoChild && <div className="mb-3 flex justify-end"><button type="button" onClick={exitChildView} className="rounded-full border border-violet-200 bg-white/90 px-4 py-2 text-xs font-black text-violet-700 shadow-card active:scale-[0.98]">← Back to Lillian</button></div>}

        <div className="grid gap-4 md:grid-cols-[1.15fr_.85fr]">
          <div className="min-w-0 rounded-[2rem] bg-white/90 p-5 shadow-card md:p-6">
            <div className="flex items-center gap-4">
              {demoChild ? <img src={youthDemoProfile.photoUrl} alt={`${firstName} profile`} className="h-20 w-20 shrink-0 rounded-full object-cover shadow-card md:h-24 md:w-24" /> : <Avatar user={currentUser} size={76} />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-extrabold text-violet-600 md:text-base">{dateLabel}</p>
                <h1 className="mt-1 text-3xl font-black text-slate-900 md:text-4xl">Hello {firstName}! 👋</h1>
                <p className="mt-1 text-sm font-extrabold text-violet-700 md:text-base">{motivationalGreeting}</p>
              </div>
              <Link to="/switch-user" className="rounded-2xl bg-violet-100 px-3 py-2 text-xs font-black text-violet-700">⇄</Link>
            </div>
          </div>

          <section className="min-w-0 rounded-[2rem] bg-cyan-100 p-5 shadow-card md:p-6">
            <h2 className="text-lg font-black text-cyan-950 md:text-xl">👋 Who is with me today?</h2>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-1 md:flex-col md:overflow-visible">
              {visibleWorkers.length === 0 && <div className="w-full rounded-2xl bg-white/70 p-4 text-sm font-bold text-cyan-800">No support worker is coming today.</div>}
              {visibleWorkers.map(({ worker, event }) => worker && <div key={event.id} className="flex min-w-[190px] items-center gap-3 rounded-2xl bg-white p-3 shadow-sm md:min-w-0"><Avatar user={worker} size={58} /><div><p className="font-black text-slate-900 md:text-lg">{worker.name}</p><p className="text-xs font-bold text-cyan-700 md:text-sm">{formatTime12Hour(event.startTime)} to {formatTime12Hour(event.endTime)}</p></div></div>)}
            </div>
          </section>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-5xl gap-4 px-4 py-4 sm:px-6 md:grid-cols-2 lg:px-8">
        <Link to="/companion" className="relative overflow-hidden rounded-[1.75rem] border border-violet-200 bg-white p-4 shadow-card active:scale-[0.99] md:col-span-2 md:p-5">
          <div className="grid items-center gap-4 sm:grid-cols-[9rem_1fr] md:grid-cols-[10rem_1fr]"><div className="mx-auto aspect-[55/67] w-32 overflow-hidden rounded-[1.25rem] bg-violet-100 shadow-sm sm:w-36 md:w-40"><YouthNixiVideo className="h-full w-full object-cover" label="Nixi on the youth Home screen" variant={homeMoment} colour={companion.colour} /></div><div className="min-w-0"><p className="text-xs font-black uppercase tracking-[0.18em] text-violet-600">My companion</p><div className="mt-2 rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3"><p className="text-base font-black text-violet-950 md:text-lg">{companionMessage}</p></div><div className="mt-3 flex items-center justify-between gap-3"><div><p className="text-xl font-black text-slate-900 md:text-2xl">Nixi</p><p className="text-sm font-bold text-slate-500">Tap to play, feed and choose a colour</p></div><span className="shrink-0 rounded-full bg-amber-100 px-3 py-2 text-sm font-black text-amber-900">🍪 {bites}</span></div></div></div>
        </Link>

        <div className="md:col-span-2"><YouthFeelingCheckIn userId={currentUser.id} /></div>

        {nextTile && <section className="rounded-[2rem] bg-white p-5 shadow-card md:p-6"><p className="text-sm font-extrabold text-violet-600">Next Up</p><div className="mt-3 flex items-center gap-4"><div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100 text-3xl md:h-20 md:w-20 md:text-4xl">{nextTile.icon}</div><div className="min-w-0 flex-1"><h2 className="text-2xl font-black text-slate-900 md:text-3xl">{nextTile.title}</h2><p className="text-sm font-bold text-slate-500 md:text-base">{nextTile.subtitle}</p></div><Link to={`/youth-routine/${nextTile.id}`} className="rounded-2xl bg-violet-600 px-5 py-4 text-sm font-black text-white md:px-6">OPEN</Link></div></section>}

        <section className="md:col-span-2">
          <h2 className="mb-3 text-xl font-black text-slate-900 md:text-2xl">My Day</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">{plan.tiles.map((tile) => <Link key={tile.id} to={`/youth-routine/${tile.id}`} className={`min-h-36 rounded-[2rem] p-5 shadow-card active:scale-[0.98] md:min-h-44 md:p-6 ${tile.id === 'morning' ? 'bg-amber-100' : tile.id === 'school' ? 'bg-sky-100' : tile.id === 'home' ? 'bg-emerald-100' : 'bg-indigo-100'}`}><span className="text-4xl md:text-5xl">{tile.icon}</span><p className="mt-3 text-xl font-black text-slate-900 md:text-2xl">{tile.title}</p><p className="mt-1 text-xs font-bold text-slate-600 md:text-sm">{tile.subtitle}</p></Link>)}</div>
        </section>

        <section className="rounded-[2rem] bg-rose-100 p-5 shadow-card md:p-6">
          <button onClick={() => setShowTellSomeone((value) => !value)} className="flex min-h-20 w-full items-center justify-between rounded-2xl bg-white px-5 py-4 text-left"><div><p className="text-xl font-black text-rose-950 md:text-2xl">🗣️ Tell someone</p><p className="mt-1 text-sm font-bold text-rose-700 md:text-base">Tap a message and the app can say it out loud.</p></div><span className="text-2xl font-black text-rose-700">{showTellSomeone ? '−' : '+'}</span></button>
          {showTellSomeone && <div className="mt-3 grid grid-cols-1 gap-3">{plan.tellSomeoneChoices.map((choice) => <button key={choice.id} onClick={() => chooseHelp(choice.id)} className={`min-h-20 rounded-2xl border-2 bg-white px-4 py-3 text-left font-black text-slate-900 ${helpRequest === choice.id ? 'border-violet-500 ring-2 ring-violet-200' : 'border-transparent'}`}><span className="mr-3 text-2xl">{choice.icon}</span>{choice.label}<span className="ml-2 text-xs font-black text-rose-600">🔊 SAY</span></button>)}{helpRequest && <p className="rounded-2xl bg-white/80 p-3 text-center text-sm font-black text-violet-800">Message spoken. Tap again to repeat it.</p>}</div>}
        </section>

        <Link to="/companion" className="rounded-[2rem] bg-gradient-to-r from-amber-100 to-violet-100 p-5 shadow-card md:p-6">
          <div className="flex items-center justify-between gap-3"><div><p className="text-sm font-black text-violet-700">⭐ Team Rewards</p><h2 className="mt-1 text-xl font-black text-slate-900 md:text-2xl">{plan.rewardGoal.icon} {plan.rewardGoal.title}</h2><p className="mt-1 text-sm font-bold text-slate-600 md:text-base">You: {points}/{plan.rewardGoal.targetPoints} points · Nixi: 🍪 {bites}</p></div><span className="text-3xl">→</span></div>
          <div className="mt-4 h-4 overflow-hidden rounded-full bg-white/80"><div className="h-full rounded-full bg-violet-600" style={{ width: `${rewardProgress}%` }} /></div>
        </Link>
      </div>
    </div>
  )
}

